import React from 'react';

/**
 * useInViewOnce
 *
 * Lightweight hook that returns a ref and a boolean that flips to true the
 * first time the element enters the viewport (and stays true afterward).
 *
 * - Uses IntersectionObserver when available; falls back to true on SSR or
 *   older browsers so components render without blocking.
 * - Useful for gating animations or auto-advancing logic so it only starts
 *   once the user has actually scrolled to the section.
 *
 * @param threshold Intersection ratio (0..1) for when "in view" should be set
 * @returns [ref, inViewOnce]
 */

export const useInViewOnce = <T extends HTMLElement = HTMLDivElement>(
  threshold: number = 0.3,
): [React.RefObject<T>, boolean] => {
  const ref = React.useRef<T | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setInView(true);
        });
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref as React.RefObject<T>, inView];
};
